import { Card, Heading, LuIcon, Separator } from "@overmap-ai/blocks"
import type { Organization } from "@overmap-ai/common"
import { useNavigate } from "@tanstack/react-router"
import { type ComponentProps, forwardRef, memo, type PropsWithoutRef, useCallback } from "react"

import { MapBackgroundContainer } from "../../components/MapBackgroundContainer/MapBackgroundContainer"
import { modelIconMapping } from "../../mappings/icons"
import { setActiveOrganizationId, useAppDispatch } from "../../store"

interface OrganizationCardProps extends Omit<PropsWithoutRef<ComponentProps<"div">>, "children"> {
	organization: Organization
}

const OrganizationCard = memo(
	forwardRef<HTMLDivElement, OrganizationCardProps>((props, ref) => {
		const { organization, ...rest } = props
		return (
			<Card variant="surface" ref={ref} size="sm" {...rest}>
				<div className="flex items-center gap-2">
					<LuIcon icon={modelIconMapping.organization} />
					<span className="truncate">{organization.name}</span>
				</div>
			</Card>
		)
	}),
)

interface OrganizationListViewProps {
	organizations: Organization[]
}

export const OrganizationListView = memo<OrganizationListViewProps>((props) => {
	const { organizations } = props
	const dispatch = useAppDispatch()
	const navigate = useNavigate()

	const handleOrganizationCardClick = useCallback(
		(organization: Organization) => {
			dispatch(setActiveOrganizationId(organization.uuid))
			void navigate({ to: "/$organizationId", params: { organizationId: organization.uuid } })
		},
		[dispatch, navigate],
	)

	return (
		<MapBackgroundContainer>
			<section className="flex flex-col gap-4 p-4">
				<Heading size="xl">Organizations</Heading>

				<Separator size="full" />

				<div className="flex h-full w-full flex-col items-center justify-center gap-2">
					{organizations.map((organization) => (
						<OrganizationCard
							className="w-full cursor-pointer"
							onClick={() => handleOrganizationCardClick(organization)}
							organization={organization}
							key={organization.uuid}
						/>
					))}
				</div>
			</section>
		</MapBackgroundContainer>
	)
})
OrganizationListView.displayName = "OrganizationListView"
